import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [suspicious, setSuspicious] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('logs');
  const [filters, setFilters] = useState({
    page: 1,
    limit: 50,
    action: '',
    success: '',
    startDate: '',
    endDate: ''
  });

  const loadLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      console.log('🔍 Carregando logs de auditoria...');
      const response = await api.get(`/audit/logs?${params}`);
      console.log('✅ Logs carregados:', response.data);
      setLogs(response.data);
    } catch (error) {
      console.error('❌ Erro ao carregar logs:', error);
    }
  }, [filters]);

  const loadStats = useCallback(async () => {
    try {
      console.log('📊 Carregando estatísticas de auditoria...');
      const response = await api.get('/audit/stats');
      console.log('✅ Estatísticas carregadas:', response.data);
      setStats(response.data);
    } catch (error) {
      console.error('❌ Erro ao carregar estatísticas:', error);
    }
  }, []);

  const loadSessions = useCallback(async () => {
    const response = await api.get('/audit/sessions');
    setSessions(response.data.sessions);
  }, []);

  const loadSuspicious = useCallback(async () => {
    const response = await api.get('/audit/suspicious');
    setSuspicious(response.data);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (activeTab === 'logs') {
        await loadLogs();
      } else if (activeTab === 'stats') {
        await loadStats();
      } else if (activeTab === 'sessions') {
        await loadSessions();
      } else if (activeTab === 'suspicious') {
        await loadSuspicious();
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  }, [activeTab, loadLogs, loadStats, loadSessions, loadSuspicious]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const revokeSession = async (sessionId) => {
    if (window.confirm('Tem certeza que deseja revogar esta sessão?')) {
      try {
        await api.delete(`/audit/sessions/${sessionId}`);
        await loadSessions();
        alert('Sessão revogada com sucesso!');
      } catch (error) {
        alert('Erro ao revogar sessão');
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'LOGIN': return 'text-green-600';
      case 'LOGOUT': return 'text-blue-600';
      case 'CREATE_USER': return 'text-purple-600';
      case 'UPDATE_USER': return 'text-yellow-600';
      case 'DELETE_USER': return 'text-red-600';
      case 'ACCESS_POWERBI': return 'text-cyan-600';
      default: return 'text-gray-600';
    }
  };

  const getStatusColor = (success) => {
    return success ? 'text-green-600' : 'text-red-600';
  };

  const renderLogs = () => (
    <div className="space-y-6">
      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold mb-4">Filtros</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <select
            value={filters.action}
            onChange={(e) => setFilters({...filters, action: e.target.value, page: 1})}
            className="border rounded px-3 py-2"
          >
            <option value="">Todas as ações</option>
            <option value="LOGIN">Login</option>
            <option value="LOGOUT">Logout</option>
            <option value="CREATE_USER">Criar usuário</option>
            <option value="UPDATE_USER">Atualizar usuário</option>
            <option value="DELETE_USER">Deletar usuário</option>
            <option value="ACCESS_POWERBI">Acesso Power BI</option>
          </select>

          <select
            value={filters.success}
            onChange={(e) => setFilters({...filters, success: e.target.value, page: 1})}
            className="border rounded px-3 py-2"
          >
            <option value="">Todos os status</option>
            <option value="true">Sucesso</option>
            <option value="false">Erro</option>
          </select>

          <input
            type="date"
            value={filters.startDate}
            onChange={(e) => setFilters({...filters, startDate: e.target.value, page: 1})}
            className="border rounded px-3 py-2"
            placeholder="Data inicial"
          />

          <input
            type="date"
            value={filters.endDate}
            onChange={(e) => setFilters({...filters, endDate: e.target.value, page: 1})}
            className="border rounded px-3 py-2"
            placeholder="Data final"
          />

          <button
            onClick={() => setFilters({page: 1, limit: 50, action: '', success: '', startDate: '', endDate: ''})}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
          >
            Limpar
          </button>
        </div>
      </div>

      {/* Tabela de Logs */}
      <div className="rf-surface border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data/Hora</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuário</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ação</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dispositivo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tempo (ms)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {logs.logs && logs.logs.length > 0 ? (
                logs.logs.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {formatDate(log.created_at)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {log.user_full_name || log.username || 'Anônimo'}
                    </td>
                    <td className={`px-4 py-3 text-sm font-medium ${getActionColor(log.action)}`}>
                      {log.action}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 font-mono">
                      {log.ip_address}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {log.device_info?.browser} - {log.device_info?.os}
                    </td>
                    <td className={`px-4 py-3 text-sm font-medium ${getStatusColor(log.success)}`}>
                      {log.success ? '✓ Sucesso' : '✗ Erro'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {log.response_time}ms
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    {logs.logs ? 'Nenhum log encontrado' : 'Carregando logs...'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Paginação */}
        {logs.pagination && (
          <div className="bg-gray-50 px-4 py-3 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Mostrando {((logs.pagination.currentPage - 1) * logs.pagination.itemsPerPage) + 1} a{' '}
              {Math.min(logs.pagination.currentPage * logs.pagination.itemsPerPage, logs.pagination.totalItems)} de{' '}
              {logs.pagination.totalItems} registros
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setFilters({...filters, page: Math.max(1, filters.page - 1)})}
                disabled={filters.page === 1}
                className="px-3 py-1 text-sm bg-white border rounded disabled:opacity-50"
              >
                Anterior
              </button>
              <span className="px-3 py-1 text-sm">
                Página {filters.page} de {logs.pagination.totalPages}
              </span>
              <button
                onClick={() => setFilters({...filters, page: Math.min(logs.pagination.totalPages, filters.page + 1)})}
                disabled={filters.page === logs.pagination.totalPages}
                className="px-3 py-1 text-sm bg-white border rounded disabled:opacity-50"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderStats = () => (
    <div className="space-y-6">
      {stats && (
        <>
          {/* Estatísticas Gerais */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-semibold text-gray-900">Total de Acessos</h3>
              <p className="text-3xl font-bold text-blue-600">{stats.overview?.total_accesses || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-semibold text-gray-900">IPs Únicos</h3>
              <p className="text-3xl font-bold text-green-600">{stats.overview?.unique_ips || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-semibold text-gray-900">Usuários Únicos</h3>
              <p className="text-3xl font-bold text-purple-600">{stats.overview?.unique_users || 0}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border">
              <h3 className="text-lg font-semibold text-gray-900">Tempo Médio</h3>
              <p className="text-3xl font-bold text-orange-600">{Math.round(stats.overview?.avg_response_time || 0)}ms</p>
            </div>
          </div>

          {/* Top IPs */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold mb-4">Top 10 IPs</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">IP Address</th>
                    <th className="text-left py-2">Acessos</th>
                    <th className="text-left py-2">Usuários</th>
                    <th className="text-left py-2">Último Acesso</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.topIPs?.map((ip, index) => (
                    <tr key={index} className="border-b">
                      <td className="py-2 font-mono">{ip.ip_address}</td>
                      <td className="py-2">{ip.access_count}</td>
                      <td className="py-2">{ip.unique_users}</td>
                      <td className="py-2">{formatDate(ip.last_access)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );

  const renderSessions = () => (
    <div className="rf-surface border overflow-hidden">
      <div className="p-4 border-b">
        <h3 className="text-lg font-semibold">Sessões Ativas</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuário</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Criada em</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expira em</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {sessions.map((session) => (
              <tr key={session.id}>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {session.full_name} ({session.username})
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 font-mono">
                  {session.ip_address}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {formatDate(session.created_at)}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  {formatDate(session.expires_at)}
                </td>
                <td className="px-4 py-3 text-sm">
                  <button
                    onClick={() => revokeSession(session.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    Revogar
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderSuspicious = () => (
    <div className="space-y-6">
      {suspicious && (
        <>
          {/* Tentativas de Login Falhadas */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold mb-4 text-red-600">🚨 Múltiplas Tentativas de Login Falhadas</h3>
            {suspicious.failedLogins?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">IP</th>
                      <th className="text-left py-2">Tentativas</th>
                      <th className="text-left py-2">Última Tentativa</th>
                      <th className="text-left py-2">Usuários Tentados</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suspicious.failedLogins.map((item, index) => (
                      <tr key={index} className="border-b">
                        <td className="py-2 font-mono text-red-600">{item.ip_address}</td>
                        <td className="py-2 font-bold text-red-600">{item.failed_attempts}</td>
                        <td className="py-2">{formatDate(item.last_attempt)}</td>
                        <td className="py-2">{item.attempted_usernames}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500">Nenhuma atividade suspeita detectada</p>
            )}
          </div>

          {/* Múltiplos IPs por Usuário */}
          <div className="bg-white p-6 rounded-lg shadow-sm border">
            <h3 className="text-lg font-semibold mb-4 text-orange-600">⚠️ Usuários com Múltiplos IPs</h3>
            {suspicious.multipleIPs?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Usuário</th>
                      <th className="text-left py-2">Qtd IPs</th>
                      <th className="text-left py-2">IPs</th>
                      <th className="text-left py-2">Último Acesso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suspicious.multipleIPs.map((item, index) => (
                      <tr key={index} className="border-b">
                        <td className="py-2">{item.username}</td>
                        <td className="py-2 font-bold text-orange-600">{item.ip_count}</td>
                        <td className="py-2 font-mono text-sm">{item.ip_addresses}</td>
                        <td className="py-2">{formatDate(item.last_access)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-gray-500">Nenhuma atividade suspeita detectada</p>
            )}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="rf-screen">
      <div>
        <div className="rf-screen__head">
          <div>
            <h1 className="rf-screen__title">Auditoria</h1>
            <p className="rf-screen__sub">Trilha de acessos e ações administrativas do sistema</p>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ margin: '18px 0' }}>
          <nav className="rf-tabs">
            {[
              { key: 'logs', label: 'Logs de Acesso' },
              { key: 'stats', label: 'Estatísticas' },
              { key: 'sessions', label: 'Sessões Ativas' },
              { key: 'suspicious', label: 'Atividades Suspeitas' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`rf-tab${activeTab === tab.key ? ' is-active' : ''}`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Conteúdo */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            {activeTab === 'logs' && renderLogs()}
            {activeTab === 'stats' && renderStats()}
            {activeTab === 'sessions' && renderSessions()}
            {activeTab === 'suspicious' && renderSuspicious()}
          </>
        )}
      </div>
    </div>
  );
};

export default AuditLogsPage; 